import Dexie, { type Table } from 'dexie';
import { sanitizeCachedNpc } from './audioDto';
import { type AppEnv, runningEnv } from '../env/appEnv';
import type { WorldElement, WorldRelationship } from '../api/types';

/**
 * Geteilter Vorlagenkatalog auf dem Geraet (W5-E, Plan 5.4).
 *
 * BEWUSST EINE EIGENE, KONTOUEBERGREIFENDE DATENBANK. Sie enthaelt
 * ausschliesslich die geteilten Vorlagen (keine personenbezogenen Daten,
 * kein Nutzerbezug: `kosmos_id` ist immer NULL) und ueberlebt deshalb
 * Abmeldung und Kontowechsel — genau das spart den erneuten Download.
 * Die Nutzer-Datenbanken (`lorath-local-u<id>`, siehe db.ts) werden beim
 * expliziten Logout weiterhin restlos geloescht; der Name hier traegt
 * absichtlich NICHT deren Praefix.
 *
 * Welche dieser Zeilen ein Konto sehen darf, steht NICHT hier, sondern als
 * Verweisliste in der Nutzer-Datenbank (`catalogVisible`). So bleibt die
 * Trennung „geteilter Inhalt" vs. „persoenliche Sicht" auch lokal sauber.
 *
 * `v1` im Namen ist die SCHEMA-Version dieser Datenbank, NICHT die
 * Katalogversion: Letztere steht im meta-Store und wird beim Wechsel
 * ersetzt. Ein versionsbehafteter Datenbankname wuerde bei jedem
 * Katalog-Neubau eine Datenbankleiche im Browser hinterlassen.
 */
export const CATALOG_DB_NAME_PRODUCTION = 'lorath-catalog-v1';
export const CATALOG_DB_NAME_BETA = 'lorath-beta-catalog-v1';

/**
 * R6-02-c: eigener Katalogname je Umgebung. Der Katalog hat keinen Nutzerbezug
 * und ueberlebt Abmeldungen — genau deshalb wuerde er sonst Beta-Vorlagen in
 * die Produktion tragen. Der Produktionsname bleibt BITGLEICH.
 */
export function catalogDbNameFor(env: AppEnv): string {
  return env === 'beta' ? CATALOG_DB_NAME_BETA : CATALOG_DB_NAME_PRODUCTION;
}

// Zur Modulladezeit ausgewertet: die laufende Umgebung steht zu diesem
// Zeitpunkt fest (Bauzeit-Konstante nativ, `<html data-app-env>` im Web) und
// aendert sich waehrend einer Sitzung nicht.
export const CATALOG_DB_NAME = catalogDbNameFor(runningEnv());

export interface CatalogMetaRecord {
  key: 'version' | 'lang' | 'fetchedAt' | 'audioDtoVersion';
  value: string | number | null;
}

export interface CatalogPayload {
  version: number;
  lang: string;
  elements: WorldElement[];
  relationships: WorldRelationship[];
}

export interface CatalogState {
  version: number;
  lang: string | null;
  elementCount: number;
}

export class LorathCatalogDatabase extends Dexie {
  elements!: Table<WorldElement, number>;
  relationships!: Table<WorldRelationship, number>;
  meta!: Table<CatalogMetaRecord, string>;

  constructor() {
    super(CATALOG_DB_NAME);
    this.version(1).stores({
      elements: 'id, type',
      relationships: 'id, source_id, target_id',
      meta: 'key',
    });
    this.version(2).stores({ elements: 'id, type', relationships: 'id, source_id, target_id', meta: 'key' }).upgrade(async (tx) => {
      await tx.table<WorldElement>('elements').toCollection().modify(row => { Object.assign(row, sanitizeCachedNpc(row)); if (row.type === 'npc') delete (row as WorldElement & { call_voice?: unknown }).call_voice; });
      await tx.table('meta').put({ key: 'audioDtoVersion', value: 0 });
    });
  }
}

let catalogDb: LorathCatalogDatabase | null = null;

export function openCatalogDb(): LorathCatalogDatabase {
  if (!catalogDb) catalogDb = new LorathCatalogDatabase();
  return catalogDb;
}

export function closeCatalogDb(): void {
  catalogDb?.close();
  catalogDb = null;
}

/** Nur fuer Tests und den Notfall — im Normalbetrieb wird der Katalog NIE geloescht. */
export async function deleteCatalogDb(): Promise<void> {
  closeCatalogDb();
  await Dexie.delete(CATALOG_DB_NAME);
}

async function readMeta(db: LorathCatalogDatabase, key: CatalogMetaRecord['key']): Promise<string | number | null> {
  const row = await db.meta.get(key);
  return row ? row.value : null;
}

/** Welche Katalogversion/Sprache liegt lokal? null = noch keine. */
export async function readCatalogState(): Promise<CatalogState | null> {
  try {
    const db = openCatalogDb();
    // Gleiche Katalogversion kann noch das alte Stimmenformat enthalten.
    if (await readMeta(db, 'audioDtoVersion') === 0) return null;
    const version = Number(await readMeta(db, 'version') ?? 0);
    if (!Number.isFinite(version) || version <= 0) return null;
    const elementCount = await db.elements.count();
    if (elementCount === 0) return null;
    const lang = await readMeta(db, 'lang');
    return { version, lang: lang == null ? null : String(lang), elementCount };
  } catch {
    // Privater Modus / gesperrte IndexedDB: der Aufrufer faellt auf den
    // klassischen Weg zurueck (Katalogzeilen kommen dann wie bisher ueber
    // die Bestandsendpunkte).
    return null;
  }
}

/** Katalog ersetzen (Version ODER Sprache hat sich geaendert). */
export async function writeCatalog(payload: CatalogPayload): Promise<void> {
  const db = openCatalogDb();
  await db.transaction('rw', [db.elements, db.relationships, db.meta], async () => {
    await db.elements.clear();
    await db.relationships.clear();
    await db.elements.bulkPut(payload.elements);
    await db.relationships.bulkPut(payload.relationships);
    await db.meta.bulkPut([
      { key: 'version', value: payload.version },
      { key: 'lang', value: payload.lang },
      { key: 'fetchedAt', value: new Date().toISOString() },
      { key: 'audioDtoVersion', value: 1 },
    ]);
  });
}

export async function readCatalogElements(): Promise<WorldElement[]> {
  try {
    return await openCatalogDb().elements.toArray();
  } catch {
    return [];
  }
}

/** Eine einzelne Katalogzeile (Detailansicht) — ohne die ganze Tabelle zu lesen. */
export async function readCatalogElement(id: number): Promise<WorldElement | undefined> {
  try {
    return await openCatalogDb().elements.get(id);
  } catch {
    return undefined;
  }
}

/** Mehrere Katalogzeilen auf einen Schlag (Weltenbaum-Knoten). */
export async function bulkGetCatalogElements(ids: number[]): Promise<WorldElement[]> {
  if (!ids.length) return [];
  try {
    const rows = await openCatalogDb().elements.bulkGet(ids);
    return rows.filter((row): row is WorldElement => !!row);
  } catch {
    return [];
  }
}

export async function readCatalogRelationships(): Promise<WorldRelationship[]> {
  try {
    return await openCatalogDb().relationships.toArray();
  } catch {
    return [];
  }
}
